// Brand Application Detail View for Admin Dashboard
// Displays comprehensive brand application details with approve/reject controls
// Used within the React routing system

import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';

const BrandDetail = ({ brandId, onNavigate }) => {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Fetch brand data
  const fetchBrand = async () => {
    try {
      console.log('🔄 Fetching brand details for:', brandId);
      setLoading(true);
      setError(null);

      if (!db) {
        throw new Error('Firebase database not initialized');
      }

      const brandDoc = await getDoc(doc(db, 'users', brandId));
      
      if (!brandDoc.exists()) {
        throw new Error('Brand application not found');
      }

      const brandData = brandDoc.data();
      setBrand({ id: brandDoc.id, ...brandData });
      console.log('✅ Brand data loaded:', brandData.companyName || 'Unknown');
    } catch (err) {
      console.error('❌ Error fetching brand:', err);
      setError(err instanceof Error ? err.message : 'Failed to load brand details');
    } finally {
      setLoading(false);
    }
  };

  // Update brand status
  const updateStatus = async (status) => {
    try {
      console.log(`🔄 Updating brand ${brandId} to ${status}...`);
      setUpdating(true);

      if (!db) {
        throw new Error('Firebase database not initialized');
      }

      const updateData = {
        status,
        updatedAt: serverTimestamp(),
        ...(status === 'approved' ? { approvedAt: serverTimestamp() } : { rejectedAt: serverTimestamp() })
      };

      await updateDoc(doc(db, 'users', brandId), updateData);
      
      // Update local state
      setBrand(prev => ({ ...prev, status, ...updateData }));
      
      // Show success message
      alert(`Brand ${status} successfully!`);
      console.log(`✅ Brand ${status} successfully`);
    } catch (err) {
      console.error('❌ Error updating brand status:', err);
      alert(`Failed to ${status} brand: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUpdating(false);
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    try {
      if (!timestamp) return 'Unknown';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium";
    switch (status) {
      case 'pending_approval':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Pending</span>;
      case 'approved':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Approved</span>;
      case 'rejected':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>Rejected</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Unknown</span>;
    }
  };

  // Get initials for avatar
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    if (brandId) {
      fetchBrand();
    }
  }, [brandId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Breadcrumb skeleton */}
          <div className="flex items-center space-x-2">
            <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
          </div>
          
          {/* Header card skeleton */}
          <div className="rounded-2xl border bg-white p-6 shadow-sm">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="flex-1 space-y-2">
                <div className="h-6 w-48 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
          
          {/* Content skeleton */}
          <div className="rounded-2xl border bg-white p-6 shadow-sm">
            <div className="space-y-4">
              <div className="h-6 w-32 bg-gray-200 rounded animate-pulse"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-2xl border bg-white p-8 shadow-sm text-center">
            <div className="h-16 w-16 mx-auto mb-4 text-red-500 text-6xl">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Brand</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-x-4">
              <button
                onClick={fetchBrand}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Try Again
              </button>
              <button
                onClick={() => onNavigate('admin')}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Back to Admin
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-2xl border bg-white p-8 shadow-sm text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Brand Not Found</h1>
            <p className="text-gray-600 mb-6">The requested brand application could not be found.</p>
            <button
              onClick={() => onNavigate('admin')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Back to Admin
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-500">
          <button
            onClick={() => onNavigate('admin')}
            className="hover:text-gray-700"
          >
            Admin
          </button>
          <span>›</span>
          <button
            onClick={() => onNavigate('admin')}
            className="hover:text-gray-700"
          >
            Brands
          </button>
          <span>›</span>
          <span className="text-gray-900 font-medium">
            {brand.companyName || brandId}
          </span>
        </nav>

        {/* Header Card */}
        <div className="rounded-2xl border bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Avatar - Clickable to view full size */}
              {brand.logoUrl ? (
                <a
                  href={brand.logoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                  title="Click to view full-size logo"
                >
                  <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden relative border-2 border-slate-200 hover:border-blue-400 transition-colors cursor-pointer">
                    <img 
                      src={brand.logoUrl} 
                      alt={brand.companyName || 'Brand logo'} 
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        if (e.target) {
                          e.target.style.display = 'none';
                          const fallback = e.target.parentElement?.querySelector('.logo-fallback');
                          if (fallback) fallback.style.display = 'flex';
                        }
                      }}
                    />
                    <div className="logo-fallback absolute inset-0 flex items-center justify-center hidden">
                      <span className="text-2xl font-bold text-blue-600">
                        {getInitials(brand.companyName || brand.contactName || 'BR')}
                      </span>
                    </div>
                  </div>
                </a>
              ) : (
                <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden relative">
                  <span className="text-2xl font-bold text-blue-600">
                    {getInitials(brand.companyName || brand.contactName || 'BR')}
                  </span>
                </div>
              )}
              
              {/* Brand Info */}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {brand.companyName || 'Unknown Company'}
                </h1>
                <div className="flex items-center space-x-4 mt-2">
                  {getStatusBadge(brand.status)}
                  <span className="text-sm text-gray-500">
                    Submitted {formatDate(brand.submittedAt || brand.createdAt)}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => updateStatus('rejected')}
                disabled={updating || brand.status !== 'pending_approval'}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? 'Updating...' : '❌ Reject'}
              </button>
              <button
                onClick={() => updateStatus('approved')}
                disabled={updating || brand.status !== 'pending_approval'}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? 'Updating...' : '✅ Approve'}
              </button>
            </div>
          </div>
        </div>

        {/* Application Details Card */}
        <div className="rounded-2xl border bg-white p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Application Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              {brand.companyName && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{brand.companyName}</dd>
                </div>
              )}
              
              {brand.industry && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Industry</dt>
                  <dd className="mt-1 text-sm text-gray-900">{brand.industry}</dd>
                </div>
              )}
              
              {brand.companySize && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Company Size</dt>
                  <dd className="mt-1 text-sm text-gray-900">{brand.companySize}</dd>
                </div>
              )}
              
              {brand.foundedYear && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Founded</dt>
                  <dd className="mt-1 text-sm text-gray-900">{brand.foundedYear}</dd>
                </div>
              )}
              
              {brand.companyWebsite && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Website</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a 
                      href={brand.companyWebsite.startsWith('http') ? brand.companyWebsite : `https://${brand.companyWebsite}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-500 hover:underline break-all"
                    >
                      {brand.companyWebsite}
                    </a>
                  </dd>
                </div>
              )}
              
              {brand.location && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Location</dt>
                  <dd className="mt-1 text-sm text-gray-900">{brand.location}</dd>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
              
              {brand.contactName && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Contact Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{brand.contactName}</dd>
                </div>
              )}
              
              {brand.contactTitle && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Title</dt>
                  <dd className="mt-1 text-sm text-gray-900">{brand.contactTitle}</dd>
                </div>
              )}
              
              {brand.email && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a 
                      href={`mailto:${brand.email}`}
                      className="text-blue-600 hover:text-blue-500"
                    >
                      {brand.email}
                    </a>
                  </dd>
                </div>
              )}
              
              {brand.phone && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900">{brand.phone}</dd>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {brand.description && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-3">About the Company</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{brand.description}</p>
            </div>
          )}

          {/* Target Audience */}
          {(brand.niches?.length > 0 || brand.targetAgeRange || brand.targetGender || brand.targetLocation) && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Target Audience</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {brand.niches?.length > 0 && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 mb-2">Interests/Niches</dt>
                    <dd className="flex flex-wrap gap-2">
                      {brand.niches.map((niche, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {niche}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
                
                {brand.targetAgeRange && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Age Range</dt>
                    <dd className="mt-1 text-sm text-gray-900">{brand.targetAgeRange}</dd>
                  </div>
                )}
                
                {brand.targetGender && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Gender</dt>
                    <dd className="mt-1 text-sm text-gray-900">{brand.targetGender}</dd>
                  </div>
                )}
                
                {brand.targetLocation && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Location</dt>
                    <dd className="mt-1 text-sm text-gray-900">{brand.targetLocation}</dd>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Campaign Goals & Budget */}
          {(brand.campaignGoals?.length > 0 || brand.budgetRange || brand.contentTypes?.length > 0) && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Preferences</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {brand.campaignGoals?.length > 0 && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 mb-2">Campaign Goals</dt>
                    <dd className="flex flex-wrap gap-2">
                      {brand.campaignGoals.map((goal, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {goal}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
                
                {brand.budgetRange && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Monthly Budget</dt>
                    <dd className="mt-1 text-sm text-gray-900">{brand.budgetRange}</dd>
                  </div>
                )}
                
                {brand.contentTypes?.length > 0 && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 mb-2">Content Types</dt>
                    <dd className="flex flex-wrap gap-2">
                      {brand.contentTypes.map((type, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {type}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sticky Footer Actions (Mobile) */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
          <div className="flex space-x-3">
            <button
              onClick={() => onNavigate('admin')}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={() => updateStatus('rejected')}
              disabled={updating || brand.status !== 'pending_approval'}
              className="flex-1 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reject
            </button>
            <button
              onClick={() => updateStatus('approved')}
              disabled={updating || brand.status !== 'pending_approval'}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandDetail;
